import { useEffect, useMemo, useState } from 'react';
import { masterCatalog } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, Plus, CheckCircle2, X, Sparkles, Package } from 'lucide-react';

/**
 * MasterCatalogGrid — shows 20 pre-seeded categories with SVG icons.
 * "Add to My Shop" flow only asks for Price + Stock — everything else copies from the master.
 */
export default function MasterCatalogGrid({ storeId, shopType, onAdded }) {
  const [items, setItems]   = useState(null);
  const [status, setStatus] = useState({});             // { master_id: {price, stock_status} }
  const [modal, setModal]   = useState(null);           // master item being added
  const [group, setGroup]   = useState('all');

  const load = async () => {
    try {
      const cat = await masterCatalog.list(shopType || 'kirana');
      const st = storeId ? await masterCatalog.statusForShop(storeId) : { added: [] };
      setItems(cat.items);
      const map = {};
      (st.added || []).forEach(p => { map[p.master_id] = p; });
      setStatus(map);
    } catch (e) { toast.error('Could not load Master Catalog'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [storeId, shopType]);

  const groups = useMemo(() => {
    if (!items) return [];
    const set = ['all', ...new Set(items.map(i => i.category_group))];
    return set;
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (group === 'all') return items;
    return items.filter(i => i.category_group === group);
  }, [items, group]);

  if (!items) {
    return <div className="card-soft p-8 text-sm text-ink/60 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading catalog…</div>;
  }

  return (
    <section data-testid="master-catalog-grid" className="space-y-4">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7A4A00] to-[#D35400] text-white grid place-items-center flex-shrink-0"><Sparkles className="w-5 h-5"/></div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A4A00]">Master Catalog</div>
          <h2 className="font-heading font-bold text-2xl">Add products in one click</h2>
          <p className="text-xs text-ink/60 mt-1">Tap any category, enter your price + stock, and you&apos;re done. Name, HSN, unit &amp; icon are already filled — no photo needed.</p>
        </div>
      </div>

      {/* Group filter */}
      <div className="flex flex-wrap gap-1.5">
        {groups.map(g => (
          <button
            key={g}
            data-testid={`catalog-group-${g}`}
            onClick={() => setGroup(g)}
            className={`px-3 py-1.5 text-xs rounded-full border transition capitalize ${group === g ? 'bg-[#7A4A00] text-white border-[#7A4A00]' : 'bg-white text-ink/70 border-border hover:border-[#7A4A00]/40'}`}
          >{g === 'all' ? 'All categories' : g}</button>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {filtered.map(item => {
          const added = status[item.id];
          return (
            <div
              key={item.id}
              data-testid={`catalog-item-${item.slug}`}
              className={`rounded-2xl border-2 p-4 flex flex-col text-center transition ${added ? 'border-[#1B5E20] bg-[#F1F8E9]' : 'border-border bg-white hover:border-[#7A4A00]/40 hover:shadow-md'}`}
            >
              <div
                className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-2 overflow-hidden"
                style={{ background: item.tint_bg || '#F5EFE0', color: item.tint_fg || '#7A4A00' }}
              >
                {item.image_url ? (
                  <img src={item.image_url} alt={item.name} className="w-full h-full object-cover rounded-full"/>
                ) : (
                  <span className="w-8 h-8 block" dangerouslySetInnerHTML={{ __html: item.icon_svg }}/>
                )}
              </div>
              <div className="font-heading font-bold text-sm">{item.name}</div>
              <div className="text-[10px] text-ink/50 uppercase tracking-wider">per {item.default_unit}</div>
              {added ? (
                <div className="mt-2 flex items-center justify-center gap-1 text-[10px] font-semibold text-[#1B5E20]">
                  <CheckCircle2 className="w-3 h-3"/> ₹{added.price} · in shop
                </div>
              ) : (
                <button
                  data-testid={`catalog-add-${item.slug}`}
                  onClick={() => setModal(item)}
                  className="mt-2 rounded-full bg-[#7A4A00] hover:bg-[#5C3800] text-white py-1.5 px-3 text-xs font-bold inline-flex items-center justify-center gap-1"
                >
                  <Plus className="w-3 h-3"/>Add to shop
                </button>
              )}
            </div>
          );
        })}
      </div>

      {modal && (
        <AddToShopModal
          storeId={storeId}
          item={modal}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); onAdded?.(); }}
        />
      )}
    </section>
  );
}

function AddToShopModal({ storeId, item, onClose, onSaved }) {
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('in_stock');
  const [rx, setRx]       = useState(!!item.prescription_required_default);
  const [expiry, setExpiry] = useState('');
  const [batch, setBatch] = useState('');
  const [busy, setBusy]   = useState(false);
  const isMedical = item.shop_type === 'medical';
  const save = async () => {
    const p = parseFloat(price);
    if (!p || p <= 0) return toast.error('Enter a valid price');
    if (isMedical && item.requires_expiry_date && !expiry) return toast.error('Please enter the expiry date');
    setBusy(true);
    try {
      const payload = { master_id: item.id, store_id: storeId, price: p, stock_status: stock };
      if (isMedical) {
        payload.prescription_required = rx;
        payload.expiry_date = expiry || null;
        payload.batch_number = batch || null;
      }
      await masterCatalog.addToShop(payload);
      toast.success(`✅ ${item.name} added to your shop`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full p-5" onClick={e => e.stopPropagation()} data-testid="add-to-shop-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full grid place-items-center overflow-hidden"
              style={{ background: item.tint_bg, color: item.tint_fg }}
            >
              {item.image_url ? <img src={item.image_url} alt={item.name} className="w-full h-full object-cover"/> :
                <span className="w-6 h-6 block" dangerouslySetInnerHTML={{ __html: item.icon_svg }}/>}
            </div>
            <div>
              <div className="text-[10px] uppercase text-ink/50">Add to shop</div>
              <h3 className="font-heading font-black text-lg">{item.name}</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5"/></button>
        </div>
        <div className="text-xs text-ink/60 mb-3">HSN {item.hsn_india} · sold per <b>{item.default_unit}</b></div>
        <div className="space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-ink/60 font-bold">Your price (₹ per {item.default_unit})</span>
            <input
              data-testid="add-shop-price"
              autoFocus type="number" step="0.01"
              value={price} onChange={e => setPrice(e.target.value)}
              placeholder="e.g., 62.00"
              className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-lg font-heading"
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-ink/60 font-bold">Stock status</span>
            <select
              data-testid="add-shop-stock"
              value={stock} onChange={e => setStock(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"
            >
              <option value="in_stock">✅ In stock</option>
              <option value="low_stock">⚠️ Low stock</option>
              <option value="out_of_stock">❌ Out of stock</option>
            </select>
          </label>
          {isMedical && (
            <div className="rounded-xl border-2 border-dashed border-[#B71C1C]/50 bg-[#FFEBEE] p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider font-bold text-[#B71C1C]">🏥 Medical / Pharmacy details</div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input data-testid="add-shop-rx" type="checkbox" checked={rx} onChange={e => setRx(e.target.checked)} className="w-4 h-4 accent-[#B71C1C]"/>
                <span className="text-sm">Prescription required (Schedule H / H1)</span>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-ink/60 font-bold">Expiry date</span>
                <input data-testid="add-shop-expiry" type="date" value={expiry} onChange={e => setExpiry(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm"/>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-ink/60 font-bold">Batch no. (optional)</span>
                <input data-testid="add-shop-batch" value={batch} onChange={e => setBatch(e.target.value)} placeholder="e.g., BX2419" className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm font-mono"/>
              </label>
            </div>
          )}
        </div>
        <button
          data-testid="add-shop-submit"
          disabled={busy}
          onClick={save}
          className="mt-4 w-full rounded-full bg-[#7A4A00] hover:bg-[#5C3800] text-white px-5 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Package className="w-4 h-4"/>}
          Add to my shop
        </button>
      </div>
    </div>
  );
}
